import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import Sidebar from '../Sidebar/Sidebar';
import WindowControls from '../Common/WindowControls';
import Dashboard from '../Dashboard/Dashboard';
import Settings from '../Settings/Settings';
import AppSettings from '../Settings/AppSettings';
import Reminders from '../Reminders/Reminders';
import Connections from '../Connections/Connections';
import Conversations from '../Conversations/Conversations';


const LayoutContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.dark.bg};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const TitleBar = styled.div`
  height: 32px;
  background: ${({ theme }) => theme.colors.dark.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  -webkit-app-region: drag;
  position: relative;
  z-index: 100; /* Ensure title bar stays on top */
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  -webkit-app-region: no-drag;
`;

const AppTitle = styled.h1`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const StatusIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ status, theme }) => {
    switch (status) {
      case 'connected': return theme.colors.status.online;
      case 'connecting': return theme.colors.status.idle;
      case 'disconnected': return theme.colors.status.offline;
      default: return theme.colors.gray[500];
    }
  }};
  
  ${({ status }) => status === 'connecting' && `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  `}
`;

const MainContainer = styled.div`
  flex: 1;
  display: flex;
  position: relative;
  overflow: hidden;
`;



const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ContentArea = styled(motion.div)`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [navigationState, setNavigationState] = useState(null);
  
  // Get connection status from AppContext
  const { 
    connectionStatus: appConnectionStatus
  } = useApp();

  React.useEffect(() => {
    // Use app connection status if available, otherwise simulate
    if (appConnectionStatus) {
      setConnectionStatus(appConnectionStatus);
    } else {
      const timer = setTimeout(() => {
        setConnectionStatus('connected');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [appConnectionStatus]);

  const handleNavigation = (section, state = null) => {
    setActiveSection(section);
    
    // Clear navigation state when navigating to different sections
    // or when explicitly setting no state
    if (state === null) {
      setNavigationState(null);
    } else {
      setNavigationState(state);
    }
  };

  const handleSidebarNavigation = (section) => {
    setActiveSection(section);
    // Always clear navigation state for sidebar navigation
    // This ensures profile navigation goes to Account Settings without highlighting
    setNavigationState(null);
  };

  const renderOtherContent = () => {
    switch (activeSection) {
      case 'user-settings':
        return <Settings onBack={() => setActiveSection('dashboard')} navigationState={navigationState} />;
      case 'account-settings':
        return <Settings onBack={() => setActiveSection('dashboard')} navigationState={null} initialTab="account" />;
      case 'app-settings':
        return <Settings onBack={() => setActiveSection('dashboard')} />;
      case 'conversations':
        return <Conversations />;
      case 'reminders':
        return <Reminders />;
      case 'connections':
        return <Connections />;
      default:
        return null;
    }
  };

  return (
    <LayoutContainer>
      <TitleBar>
        <TitleSection>
          <StatusIndicator status={connectionStatus} />
          <AppTitle>SARVIK Assistant</AppTitle>
        </TitleSection>
        <WindowControls />
      </TitleBar>
      

      
      <MainContainer>
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          activeSection={activeSection}
          onSectionChange={handleSidebarNavigation}
        />
        
        <ContentWrapper>
          {/* Keep Dashboard always mounted but hidden when not active - preserves all state */}
          <ContentArea
            style={{ 
              display: activeSection === 'dashboard' ? 'flex' : 'none',
              opacity: activeSection === 'dashboard' ? 1 : 0
            }}
          >
            <Dashboard onNavigate={handleNavigation} />
          </ContentArea>
          
          {/* Render other sections with animation only when active */}
          {activeSection !== 'dashboard' && (
            <ContentArea
              key={activeSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderOtherContent()}
            </ContentArea>
          )}
        </ContentWrapper>
      </MainContainer>
    </LayoutContainer>
  );
};

// Coming Soon Placeholder Component
const ComingSoonContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.dark.bg};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ComingSoonTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const ComingSoonDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  text-align: center;
  max-width: 400px;
  line-height: 1.6;
`;

const ComingSoonIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: 2rem;
`;

const ComingSoonPlaceholder = ({ title }) => (
  <ComingSoonContainer>
    <ComingSoonIcon>🚧</ComingSoonIcon>
    <ComingSoonTitle>{title}</ComingSoonTitle>
    <ComingSoonDescription>
      This feature is coming soon! We're working hard to bring you the best AI assistant experience.
    </ComingSoonDescription>
  </ComingSoonContainer>
);

export default AppLayout;
